"use client";

import { memo, useState, useCallback } from 'react';
import styles from './SharedFilterOption.module.scss';
import Checkbox from '@components/atoms/Checkbox';
import Button from '@components/atoms/Button';
import Image from 'next/image';
import assetLoader from '@utils/internalImageLoder';
import { FilterOption } from '@helpers/filterConfigHelpers';

export interface SharedFilterOptionProps {
  option: FilterOption;
  selectedValues: string[];
  onSelect: (value: string) => void;
  showSubcategories?: boolean;
  className?: string;
  subcategoryClassName?: string;
  subcategoryToggleClassName?: string;
}

const SharedFilterOption = memo<SharedFilterOptionProps>(({
  option,
  selectedValues,
  onSelect,
  showSubcategories = true,
  className = '',
  subcategoryClassName = '',
  subcategoryToggleClassName = ''
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const hasSubcategories = option.subcategories && option.subcategories.length > 0;
  const isParentSelected = selectedValues.includes(option.value);
  const shouldShowSubcategories = showSubcategories && hasSubcategories && isExpanded && !isParentSelected;

  const handleSubcategoryToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const handleOptionSelect = useCallback(() => {
    onSelect(option.value);
  }, [onSelect, option.value]);

  const handleSubOptionSelect = useCallback((subValue: string) => {
    onSelect(subValue);
  }, [onSelect]);

  return (
    <div className={`${styles.optionWrapper} ${className}`}>
      <div className={styles.optionRow}>
        <Checkbox
          label={option.label}
          checked={selectedValues.includes(option.value)}
          onChange={handleOptionSelect}
        />
        {hasSubcategories && !isParentSelected && (
          <Button
            className={`${styles.subcategoryToggle} ${subcategoryToggleClassName}`}
            onClick={handleSubcategoryToggle}
            aria-label={`Toggle ${option.label} subcategories`}
          >
            {isExpanded ? (
              <Image
                src={`${assetLoader()}/icons/Chevron_up_dark.svg`}
                alt="Collapse subcategories"
                width={20}
                height={20}
                loading="lazy"
              />
            ) : (
              <Image
                src={`${assetLoader()}/icons/Chevron_down_dark.svg`}
                alt="Expand subcategories"
                width={20}
                height={20}
                loading="lazy"
              />
            )}
          </Button>
        )}
      </div>

      {shouldShowSubcategories && option.subcategories && (
        <div className={`${styles.subcategoriesContainer} ${subcategoryClassName}`}>
          {option.subcategories.map((subOption) => (
            <Checkbox
              key={subOption.value}
              label={subOption.label}
              checked={selectedValues.includes(subOption.value)}
              onChange={() => handleSubOptionSelect(subOption.value)}
              className={styles.subcategoryCheckbox}
            />
          ))}
        </div>
      )}
    </div>
  );
});

SharedFilterOption.displayName = 'SharedFilterOption';

export default SharedFilterOption; 